import React, {useState, useEffect} from 'react';
import dynamic from 'next/dynamic';
import getConfig from 'next/config';

import HtmlText from '~/src/components/htmlText/HtmlText';
import Quote from '~/src/components/quote/Quote';
import List from '~/src/components/list/List';
import Ad from '~/src/components/ad/Ad';
import VideoPlayer from '~/src/components/video/player/VideoPlayer';
import Authors from '~/src/components/authors/Authors';
import Agencies from '~/src/components/agencies/Agencies';
import PublishDate from '~/src/components/publishDate/PublishDate';
import InfoBox from '~/src/components/infoBox/InfoBox';
import DynamicTeaserBox from '~/src/components/dynamicTeaserBox/DynamicTeaserBox';
import Poll from '~/src/components/poll/Poll';
import DebugOutput from '~/src/components/debug/output/DebugOutput';
import Slideshow from '~/src/components/slideshow/Slideshow';
import Crosshead from '~/src/components/crosshead/Crosshead';
import ImageWrapper from '~/src/components/image/wrapper/ImageWrapper';
import TextBlockArray from '~/src/components/textBlockArray/TextBlockArray';
import EmbedIframe from '~/src/components/embed/iframe/EmbedIframe';
import InterviewSegment from '~/src/components/interviewSegment/InterviewSegment';

import {showAd} from '~/src/utilities/helpers/ad';
import {isEnabled} from '~/src/utilities/helpers/feature';
import {Feature} from '~/src/utilities/constants/feature';
import {ElementTypes} from '~/src/utilities/constants/types';
import {withErrorBoundary} from '~/src/components/errorBoundary/ErrorBoundary';
import rollbar from '~/src/utilities/libs/rollbar';

const {publicRuntimeConfig: config} = getConfig();

const Embed = dynamic({
    loader: () => import('~/src/components/embed/Embed'),
    loading: () => false,
    ssr: false
});

const Element = (props) => {
    switch (props.type) {
        case ElementTypes.TITLE:
            return <HtmlText {...props} as="h1" />;
        case ElementTypes.LEAD:
            return <HtmlText {...props} as="h2" />;
        case ElementTypes.CROSSHEAD:
            return <Crosshead {...props} />;
        case ElementTypes.UNORDERED_LIST:
        case ElementTypes.ORDERED_LIST:
        case ElementTypes.HTML_TEXT:
            return <HtmlText {...props} as="p" />;
        case ElementTypes.IMAGE:
            if (!props.image) {
                return config.debug.showComponents && <DebugOutput message={`Image data missing:\n${JSON.stringify(props, null, 2)}`} />;
            }
            return <ImageWrapper {...props.image} format={props.format} isTitleElement={props.position === 1} />;
        case ElementTypes.QUOTE:
            return <Quote {...props} />;
        case ElementTypes.LIST:
            return <List {...props} />;
        case ElementTypes.SEPARATOR:
            return <hr />;
        case ElementTypes.VIDEO:
        case ElementTypes.EMBED:
            return <Embed {...props} />;
        case ElementTypes.AD:
            const [renderAd, setRenderAd] = useState(false);

            process.browser &&
                useEffect(() => {
                    setRenderAd(!props.availableFor || showAd(props.availableFor));
                }, []);

            if (!renderAd) {
                return null;
            }

            return <Ad {...props} />;
        case ElementTypes.SLIDESHOW:
            return <Slideshow {...props} />;
        case ElementTypes.DYNAMIC_TEASER:
            return <DynamicTeaserBox {...props} />;
        case ElementTypes.CONTAINER:
        case ElementTypes.INFOBOX:
            return <InfoBox {...props} />;
        case ElementTypes.POLL:
            return isEnabled(Feature.POLL) && <Poll {...props} />;
        case ElementTypes.VIDEOCMS:
            if (!props.content || !props.content.elements) {
                return config.debug.showComponents && <DebugOutput message={`Video data missing:\n${JSON.stringify(props, null, 2)}`} />;
            }
            return (
                <VideoPlayer
                    {...props.content.elements[0]}
                    caption={props.caption}
                    captionTitle={props.captionTitle}
                    credit={props.credit}
                    content={{meta: props.meta}}
                    position={props.position}
                    fallbackUrl={props.url}
                    storyId={props.id}
                />
            );
        case ElementTypes.AUTHORS:
            return <Authors {...props} />;
        case ElementTypes.AGENCIES:
            return <Agencies {...props} />;
        case ElementTypes.PUBLISH_DATE:
            return <PublishDate {...props} />;
        case ElementTypes.TITLE_HEADER:
            return <HtmlText {...props} as="h2" />;
        case ElementTypes.SLIDE_PLACEHOLDER:
            return null; // TODO: PD-447 create placeholder for slideshows
        case ElementTypes.TEXT_BLOCK_ARRAY:
            return <TextBlockArray {...props} />;
        case ElementTypes.LIVESTREAM:
            return <EmbedIframe {...props} />;
        case ElementTypes.INTERVIEW_SEGMENT:
            return <InterviewSegment {...props} />;
        default:
            rollbar.info(`[Element] Type is not supported: ${props.type}`);
            break;
    }

    return null;
};

export default withErrorBoundary(Element, 'Element');
